import React, { useMemo, useRef } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { OpportunityTile as OpportunityTileType } from '../../../../../../useProductAnalysis.logic';
import './OpportunityTile.styles.scss';

interface OpportunityTileProps {
  tile: OpportunityTileType;
  period: string;
}

/**
 * Truncates text after a specified number of words and adds ellipsis
 * @param text - The text to truncate
 * @param wordLimit - Maximum number of words before truncation
 * @returns Truncated text with ellipsis if exceeded word limit
 */
const truncateByWords = (text: string, wordLimit: number): string => {
  if (!text || typeof text !== 'string') return '';
  const trimmedText = text.trim();
  if (!trimmedText) return '';

  // Split by whitespace and filter out empty strings
  const words = trimmedText.split(/\s+/).filter(word => word.length > 0);

  if (words.length <= wordLimit) {
    return trimmedText; // Return original trimmed text if within limit
  }

  return words.slice(0, wordLimit).join(' ') + '...';
};

const OpportunityTile: React.FC<OpportunityTileProps> = ({ tile, period: _period }) => {
  const _tooltipRef = useRef<Tooltip>(null);
  const tileRef = useRef<HTMLDivElement>(null);

  // Check if this is the Contract Types card (case-insensitive, trimmed)
  const isContractTypesCard = useMemo(() => {
    return tile.label?.trim().toLowerCase() === 'contract types';
  }, [tile.label]);

  // Check if value is 'No contracts' (case-insensitive, trimmed)
  const isNoContracts = useMemo(() => {
    if (!isContractTypesCard || !tile.value) return false;
    return tile.value.trim().toLowerCase() === 'no contracts';
  }, [tile.value, isContractTypesCard]);

  // Truncate value for Contract Types card (8 words max)
  // Show 'N/A' if value is 'No contracts'
  const displayValue = useMemo(() => {
    if (isContractTypesCard) {
      if (isNoContracts) {
        return 'N/A';
      }
      return truncateByWords(tile.value, 2);
    }
    return tile.value;
  }, [tile.value, isContractTypesCard, isNoContracts]);

  // Check if truncation occurred by comparing display value with original
  // No truncation if value is 'No contracts'
  const isTruncated = useMemo(() => {
    if (!isContractTypesCard || !tile.value || isNoContracts) return false;
    // Check if displayValue is different from original (meaning truncation occurred)
    return displayValue !== tile.value.trim();
  }, [displayValue, tile.value, isContractTypesCard, isNoContracts]);

  // Generate unique ID for tooltip target using a hash of the value
  const tooltipTargetId = useMemo(() => {
    if (!isContractTypesCard) return undefined;
    // Create a simple hash from the value for uniqueness
    const hash = tile.value.split('').reduce((acc, char) => {
      return (acc << 5) - acc + char.charCodeAt(0);
    }, 0);
    return `contract-types-tile-${Math.abs(hash)}`;
  }, [tile.value, isContractTypesCard]);

  return (
    <>
      <div
        ref={tileRef}
        className={`opportunity-tile ${isContractTypesCard ? 'contract-types-card' : ''}`}
        id={tooltipTargetId}
        data-pr-tooltip={isTruncated ? tile.value : undefined}
        data-pr-position={isTruncated ? 'right' : undefined}
      >
        <div className="tile-value">
          <span
            className={`tile-value-text ${isContractTypesCard && isTruncated ? 'truncated' : ''}`}
          >
            {displayValue}
          </span>
        </div>
        <div className="tile-label">
          <span className="tile-label-text">{tile.label}</span>
        </div>
      </div>
      {/* Tooltip for Contract Types card - positioned to the right */}
      {/* Render Tooltip only when truncation occurred (not for 'No contracts' case) */}
      {/* {isContractTypesCard && tooltipTargetId && !isNoContracts && (
        <Tooltip
          ref={tooltipRef}
          target={`#${tooltipTargetId}`}
          position="right"
          className="contract-types-tooltip"
          showDelay={200}
          hideDelay={100}
        />
      )} */}
    </>
  );
};

export default OpportunityTile;
